import { defineStore } from "pinia"
import { HEAVYBIT_ORG_ID } from "@auger/identity"
import { isViewSettingEntry, type ViewSettingEntry } from "@auger/ui/composables/viewSettings/entry"
import type { AuthProfile, AuthSession } from "@/identity/auth"
import { client, DEV_BYPASS_ROLE } from "@/init/apollo"
import { getUserById } from "@/stores/users"

interface UserRecord {
  email: string | null
  name: string | null
  partner: boolean
  record_id: number | null
  record: Record<string, unknown> | null
}

interface UserData {
  fireflies_token?: string | null
  pipedrive_token?: string | null
  airtable_token?: string | null
  [key: string]: unknown
}

interface UserState {
  authenticated: boolean
  loggingOut: boolean
  profile: AuthProfile
  user: UserRecord
  userData: UserData | null
  viewSettings: Record<string, ViewSettingEntry>
  userId: string | null
  /** The person record id of the signed-in member (x-hasura-person-id). */
  personId: number | null
  /**
   * The Hasura roles of the session (x-hasura-allowed-roles), which are the
   * member's Stytch roles. Admin is heavybit_admin here, never users.admin.
   */
  roles: string[]
}

/** The signed-in person's record id: the session's x-hasura-person-id. */
export const getPersonId = (): number | null => useUserStore().personId

/** The signed-in person's record id. Kept as the name callers know; it is
 *  the person id from the session, no longer users.record_id. */
export const getUserRecordId = (): number | null =>
  useUserStore().personId ?? useUserStore().user?.record_id ?? null

/**
 * Whether the signed-in member owns a row: its owner pair is this person in
 * the Heavybit organization, the same test as the Hasura owner rule. The app
 * signs in staff only, so the session's org is always Heavybit's.
 */
export const isOwnedByMe = (
  row: { owner_person_id?: number | null; owner_org_id?: number | null } | null | undefined
): boolean => {
  const personId = getUserRecordId()
  return (
    personId != null && row?.owner_person_id === personId && row?.owner_org_id === HEAVYBIT_ORG_ID
  )
}

export const isLoggedIn = (): boolean => !!useUserStore().userId

export const useUserStore = defineStore("user", {
  state: (): UserState => ({
    authenticated: false,
    loggingOut: false,
    profile: {},
    user: {
      email: null,
      name: null,
      partner: false,
      record_id: null,
      record: null,
    },
    userData: null,
    viewSettings: {},
    userId: null,
    personId: null,
    roles: [],
  }),

  getters: {
    hadAuthenticatedSession: (state): boolean => state.authenticated || state.loggingOut,
    email: (state): string | null | undefined => state.user?.email,
    name: (state): string | null | undefined => state.user?.name,
    isAdmin: (state): boolean => state.roles.includes("heavybit_admin"),
    // A partner is a person on the partners list (the heavybit_partner role).
    isPartner: (state): boolean => !!getUserById(state.personId)?.partner,
    firefliesReady: (state): boolean => !!state.userData?.fireflies_token,
    firefliesToken: (state) => state.userData?.fireflies_token,
    pipedriveReady: (state): boolean => !!state.userData?.pipedrive_token,
    pipedriveToken: (state) => state.userData?.pipedrive_token,
    airtableReady: (state): boolean => !!state.userData?.airtable_token,
    airtableToken: (state) => state.userData?.airtable_token,
  },

  actions: {
    updateAuthInfo({ loggedIn, profile }: AuthSession) {
      this.authenticated = loggedIn
      this.profile = profile ?? {}

      const claims = profile?.["https://hasura.io/jwt/claims"]
      const userId = claims?.["x-hasura-user-id"]
      this.userId = userId || null
      const personId = Number(claims?.["x-hasura-person-id"])
      this.personId = Number.isInteger(personId) && personId > 0 ? personId : null
      const roles = claims?.["x-hasura-allowed-roles"]
      this.roles = Array.isArray(roles)
        ? roles.filter((role): role is string => typeof role === "string")
        : []
    },

    updateProperties(payload: Partial<UserRecord>) {
      this.$patch((state) => {
        Object.assign(state.user, payload)
      })
    },

    updateUserData(data: UserData | null) {
      this.userData = data
    },

    // `all` comes straight off the network (the login fetch) — filter out
    // anything that doesn't match the envelope shape rather than trusting a
    // malformed row into every `read()` call site's cast downstream.
    updateViewSettings(all: Record<string, unknown> | null | undefined) {
      const entries = Object.entries(all ?? {})
      const valid = entries.filter(([, entry]) => isViewSettingEntry(entry))
      if (valid.length < entries.length) {
        const droppedKeys = entries
          .filter(([, entry]) => !isViewSettingEntry(entry))
          .map(([k]) => k)
        // A real server-side corruption should leave a trace — silently
        // dropping it is the "reported months later, no diagnostic trail"
        // failure mode this guard exists to prevent for read()'s callers,
        // not to also become for whoever investigates the report.
        console.error("Dropping malformed user_data.view_settings entries:", droppedKeys)
      }
      this.viewSettings = Object.fromEntries(valid) as Record<string, ViewSettingEntry>
    },

    setViewSetting(key: string, entry: ViewSettingEntry) {
      this.viewSettings = { ...this.viewSettings, [key]: entry }
    },

    removeViewSetting(key: string) {
      const { [key]: _, ...rest } = this.viewSettings
      this.viewSettings = rest
    },

    setForcedUser(id: string, personId?: string | null) {
      this.authenticated = true
      this.userId = id
      const person = Number(personId)
      this.personId = Number.isInteger(person) && person > 0 ? person : null
      this.roles = [DEV_BYPASS_ROLE]
    },

    startLogout() {
      this.loggingOut = true
      this.userData = null
      this.viewSettings = {}
      client.clearStore().catch((err: Error) => {
        console.error("Failed to clear Apollo cache on logout:", err)
      })
    },
  },
})
